import { Link } from 'react-router-dom';
import { useServiceFlash } from '../../context/LiveContext';
import { Service } from '../../types';
import { UptimeBar } from './UptimeBar';
import { statusColors, statusLabels, formatDuration, formatRelativeTime, formatUptime, displayUrl } from '../../utils/status';

interface Props {
  service: Service;
}

/**
 * Dense row for the default dashboard view. A three-column card grid is fine
 * for ten services and hopeless for fifty — rows put the whole fleet on one
 * screen so a failure is found by scanning, not scrolling.
 */
export function ServiceRow({ service }: Props) {
  const flashing = useServiceFlash(service.id);

  return (
    <Link
      to={`/services/${service.id}`}
      className={`${flashing ? 'animate-flash' : ''} group grid grid-cols-[3px_minmax(0,2fr)_minmax(0,3fr)_auto] sm:grid-cols-[3px_minmax(0,2fr)_minmax(0,3fr)_5rem_5rem_6rem] items-center gap-x-3 sm:gap-x-4 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500`}
    >
      {/* Status as form, not only colour */}
      <span
        className={`self-stretch rounded-sm ${statusColors[service.status]}`}
        aria-hidden="true"
      />

      <div className="min-w-0">
        <p className="text-sm font-medium truncate group-hover:underline">{service.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">{service.type === 'http' ? service.url : displayUrl(service.url, service.type)}</p>
      </div>

      <div className="min-w-0">
        <UptimeBar checks={service.recent_checks || []} count={40} />
      </div>

      <span className="text-xs font-mono tabular-nums text-right text-gray-600 dark:text-gray-300">
        {formatUptime(service.uptime_24h)}
      </span>

      <span className="hidden sm:block text-xs font-mono tabular-nums text-right text-gray-500 dark:text-gray-400">
        {service.avg_response_time !== null && service.avg_response_time !== undefined
          ? formatDuration(service.avg_response_time)
          : '—'}
      </span>

      <span className="hidden sm:block text-xs text-right text-gray-500 dark:text-gray-400">
        <span className="sr-only">{statusLabels[service.status]}, last checked </span>
        {service.last_checked_at ? formatRelativeTime(service.last_checked_at) : 'never'}
      </span>
    </Link>
  );
}

export function ServiceRowHeader() {
  return (
    <div
      className="hidden sm:grid grid-cols-[3px_minmax(0,2fr)_minmax(0,3fr)_5rem_5rem_6rem] gap-x-4 px-3 py-2 text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
      aria-hidden="true"
    >
      <span />
      <span>Service</span>
      <span>Last 40 checks</span>
      <span className="text-right">Uptime</span>
      <span className="text-right">Response</span>
      <span className="text-right">Checked</span>
    </div>
  );
}
